import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Loader, AlertCircle } from 'lucide-react';
import { Secretary, secretariesApi } from '../../services/secretaryAssignmentService';
import CollapsibleSection from '../common/CollapsibleSection';
import SecretaryPicker from './SecretaryPicker';

interface Props {
  entityType: 'lead' | 'client';
  entityId: number;
  /** ברירת מחדל: מקופל. ניתן לאלץ פתיחה לעמוד פרטים מורחב. */
  defaultOpen?: boolean;
  /** כותרת חלופית במידת הצורך */
  title?: string;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const LoadingRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #6c757d;
  font-size: 0.85rem;
`;

const ErrorRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #dc2626;
  font-size: 0.85rem;
`;

const Spinner = styled(Loader)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SecretaryManagementSection: React.FC<Props> = ({
  entityType,
  entityId,
  defaultOpen = false,
  title = 'מזכירות עם גישה',
}) => {
  const [secretaries, setSecretaries] = useState<Secretary[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedOnce, setLoadedOnce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = entityType === 'lead'
        ? await secretariesApi.getByLead(entityId)
        : await secretariesApi.getByClient(entityId);
      setSecretaries(list);
      setLoadedOnce(true);
    } catch (err: any) {
      const message = err?.response?.data?.message
        || err?.response?.data
        || 'שגיאה בטעינת המזכירות';
      setError(typeof message === 'string' ? message : 'שגיאה בטעינת המזכירות');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (defaultOpen && !loadedOnce) {
      load();
    }
  }, [defaultOpen, loadedOnce, load]);

  const ensureLoaded = () => {
    if (!loadedOnce && !loading) load();
  };

  const extractErrorMessage = (err: any): string => {
    if (err?.response?.status === 403) return 'אין לך הרשאה לבצע פעולה זו';
    const message = err?.response?.data?.message || err?.response?.data;
    return typeof message === 'string' ? message : 'שגיאה בביצוע הפעולה';
  };

  const handlePickerChange = async (next: Secretary[]) => {
    const currentIds = new Set(secretaries.map(s => s.id));
    const nextIds = new Set(next.map(s => s.id));
    const added = next.filter(s => !currentIds.has(s.id));
    const removed = secretaries.filter(s => !nextIds.has(s.id));

    if (added.length === 0 && removed.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      for (const secretary of added) {
        if (entityType === 'lead') {
          await secretariesApi.assignToLead(entityId, secretary.id);
        } else {
          await secretariesApi.assignToClient(entityId, secretary.id);
        }
      }
      for (const secretary of removed) {
        if (entityType === 'lead') {
          await secretariesApi.removeFromLead(entityId, secretary.id);
        } else {
          await secretariesApi.removeFromClient(entityId, secretary.id);
        }
      }
      await load();
    } catch (err: any) {
      setError(extractErrorMessage(err));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleAddAll = async () => {
    setBusy(true);
    setError(null);
    try {
      if (entityType === 'lead') {
        await secretariesApi.assignAllToLead(entityId);
      } else {
        await secretariesApi.assignAllToClient(entityId);
      }
      await load();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClickCapture={ensureLoaded}>
      <CollapsibleSection title={title} defaultOpen={defaultOpen}>
        <Body>
          {loading && (
            <LoadingRow>
              <Spinner size={14} />
              טוען מזכירות...
            </LoadingRow>
          )}
          {error && (
            <ErrorRow>
              <AlertCircle size={14} />
              {error}
            </ErrorRow>
          )}
          {busy && !loading && (
            <LoadingRow>
              <Spinner size={14} />
              מעדכן...
            </LoadingRow>
          )}
          <SecretaryPicker
            selectedSecretaries={secretaries}
            onChange={handlePickerChange}
            onAddAll={handleAddAll}
            disabled={busy || loading}
          />
        </Body>
      </CollapsibleSection>
    </div>
  );
};

export default SecretaryManagementSection;
